import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, FormField, Heading, Input, SelectField, Skeleton, Text } from "@lendcord/concord";
import { useGetLoanTermsQuery, useUpdateLoanTermsMutation } from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { isoToWireDate, wireDateToIso } from "@/app/dateFormat";
import { PHONE_REGEX, toIndianE164 } from "@/auth/validators";
import type { LoanReferenceRequest, LoanType, MandateType } from "@/features/onboarding/onboarding.types";
import type { NomineeRelationship } from "@/app/profile/profile.types";

interface LoanTermsStepProps {
  applicationId: string;
}

interface ReferenceFormState {
  name: string;
  relation: NomineeRelationship | "";
  contact: string;
}

interface FormState {
  loanType: LoanType | "";
  roi: string;
  mandateType: MandateType | "";
  numberOfInstallments: string;
  installmentAmount: string;
  installmentStartDate: string;
  processingFeePercent: string;
  installmentPenalty: string;
  references: [ReferenceFormState, ReferenceFormState];
}

const EMPTY_REFERENCE: ReferenceFormState = { name: "", relation: "", contact: "" };
const EMPTY_FORM: FormState = {
  loanType: "",
  roi: "",
  mandateType: "",
  numberOfInstallments: "",
  installmentAmount: "",
  installmentStartDate: "",
  processingFeePercent: "",
  installmentPenalty: "",
  references: [EMPTY_REFERENCE, EMPTY_REFERENCE],
};

const RELATIONSHIP_LABEL_IDS: Record<NomineeRelationship, string> = {
  SPOUSE: "lender.profile.relationshipSpouse",
  PARENT: "lender.profile.relationshipParent",
  CHILD: "lender.profile.relationshipChild",
  SIBLING: "lender.profile.relationshipSibling",
  OTHER: "lender.profile.relationshipOther",
};

// Form 2 (loan-terms half only — see the plan's explicit scope note on the
// KYC-edit half, .../profile, being a separate piece). Always mounts already
// "in edit mode" — there's no separate view state to toggle into, unlike
// KycDetails/BankAccountSection which stay read-only until a button click.
export function LoanTermsStep({ applicationId }: LoanTermsStepProps) {
  const intl = useIntl();
  const { data, isLoading: isLoadingTerms } = useGetLoanTermsQuery(applicationId);
  const [updateLoanTerms, { isLoading: isSubmitting }] = useUpdateLoanTermsMutation();

  const [hasSeeded, setHasSeeded] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | "reference1", string>>>({});
  const [formError, setFormError] = useState<string | undefined>();

  useEffect(() => {
    if (!data || hasSeeded) return;
    const seededReferences: [ReferenceFormState, ReferenceFormState] = [EMPTY_REFERENCE, EMPTY_REFERENCE];
    data.references.forEach((reference, index) => {
      if (index < 2) {
        seededReferences[index] = { name: reference.name, relation: reference.relation, contact: reference.contact };
      }
    });
    setForm({
      loanType: data.loanType ?? "",
      roi: data.roi != null ? String(data.roi) : "",
      mandateType: data.mandateType ?? "",
      numberOfInstallments: data.numberOfInstallments != null ? String(data.numberOfInstallments) : "",
      installmentAmount: data.installmentAmount != null ? String(data.installmentAmount) : "",
      installmentStartDate: data.installmentStartDate ? wireDateToIso(data.installmentStartDate) : "",
      processingFeePercent: data.processingFeePercent != null ? String(data.processingFeePercent) : "",
      installmentPenalty: data.installmentPenalty != null ? String(data.installmentPenalty) : "",
      references: seededReferences,
    });
    setHasSeeded(true);
  }, [data, hasSeeded]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateReference(index: 0 | 1, patch: Partial<ReferenceFormState>) {
    setForm((prev) => {
      const nextReferences = [...prev.references] as [ReferenceFormState, ReferenceFormState];
      nextReferences[index] = { ...nextReferences[index], ...patch };
      return { ...prev, references: nextReferences };
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof errors = {};
    if (!form.loanType) nextErrors.loanType = intl.formatMessage({ id: "lender.onboarding.review.loanTypeRequiredError" });
    if (!form.roi) nextErrors.roi = intl.formatMessage({ id: "lender.onboarding.review.roiRequiredError" });
    if (!form.mandateType) nextErrors.mandateType = intl.formatMessage({ id: "lender.onboarding.review.mandateTypeRequiredError" });
    if (!form.numberOfInstallments) {
      nextErrors.numberOfInstallments = intl.formatMessage({ id: "lender.onboarding.review.installmentsRequiredError" });
    }
    if (!form.installmentStartDate) {
      nextErrors.installmentStartDate = intl.formatMessage({ id: "lender.onboarding.review.startDateRequiredError" });
    }
    if (!form.processingFeePercent) {
      nextErrors.processingFeePercent = intl.formatMessage({ id: "lender.onboarding.review.processingFeeRequiredError" });
    }
    if (!form.installmentPenalty) {
      nextErrors.installmentPenalty = intl.formatMessage({ id: "lender.onboarding.review.penaltyRequiredError" });
    }
    const [reference1] = form.references;
    if (!reference1.name.trim() || !reference1.relation || !PHONE_REGEX.test(reference1.contact)) {
      nextErrors.reference1 = intl.formatMessage({ id: "lender.onboarding.review.referenceOneRequiredError" });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const references: LoanReferenceRequest[] = [
      { name: reference1.name.trim(), relation: reference1.relation as NomineeRelationship, contact: toIndianE164(reference1.contact) },
    ];
    const [, reference2] = form.references;
    if (reference2.name.trim() || reference2.relation || reference2.contact) {
      references.push({
        name: reference2.name.trim(),
        relation: reference2.relation as NomineeRelationship,
        contact: toIndianE164(reference2.contact),
      });
    }

    updateLoanTerms({
      id: applicationId,
      payload: {
        loanType: form.loanType as LoanType,
        roi: Number(form.roi),
        mandateType: form.mandateType as MandateType,
        numberOfInstallments: Number(form.numberOfInstallments),
        ...(form.installmentAmount ? { installmentAmount: Number(form.installmentAmount) } : {}),
        installmentStartDate: isoToWireDate(form.installmentStartDate),
        processingFeePercent: Number(form.processingFeePercent),
        installmentPenalty: Number(form.installmentPenalty),
        references,
      },
    })
      .unwrap()
      .catch((error) => {
        setFormError(
          isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.review.loanTermsSubmitError" }),
        );
      });
  }

  if (isLoadingTerms || !hasSeeded) {
    return (
      <div className="mt-6 flex flex-col gap-4 rounded-md border border-border bg-surface-raised p-6">
        <Skeleton variant="text" className="h-5 w-40" />
        <Skeleton variant="rect" className="h-10 w-full" />
        <Skeleton variant="rect" className="h-10 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-6 rounded-md border border-border bg-surface-raised">
      {formError && (
        <div className="px-6 pt-6">
          <AlertBanner status="error">{formError}</AlertBanner>
        </div>
      )}

      <section className="px-6 py-4">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.loanTermsHeading" />
        </Heading>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <SelectField
            label={intl.formatMessage({ id: "lender.onboarding.review.loanTypeLabel" })}
            placeholder={intl.formatMessage({ id: "lender.onboarding.review.loanTypePlaceholder" })}
            value={form.loanType}
            onValueChange={(value) => updateField("loanType", value as LoanType)}
            error={errors.loanType}
            required
            options={[
              { value: "INTEREST_ONLY", label: intl.formatMessage({ id: "lender.onboarding.review.loanTypeInterestOnly" }) },
              { value: "EMI", label: intl.formatMessage({ id: "lender.onboarding.review.loanTypeEmi" }) },
            ]}
          />
          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.roiLabel" })}
            error={errors.roi}
            required
          >
            <Input
              type="number"
              value={form.roi}
              onChange={(event) => updateField("roi", event.target.value)}
              min={0}
              step="any"
            />
          </FormField>
          <SelectField
            label={intl.formatMessage({ id: "lender.onboarding.review.mandateTypeLabel" })}
            placeholder={intl.formatMessage({ id: "lender.onboarding.review.mandateTypePlaceholder" })}
            value={form.mandateType}
            onValueChange={(value) => updateField("mandateType", value as MandateType)}
            error={errors.mandateType}
            required
            options={[
              { value: "MONTHLY", label: intl.formatMessage({ id: "lender.onboarding.review.mandateMonthly" }) },
              { value: "WEEKLY", label: intl.formatMessage({ id: "lender.onboarding.review.mandateWeekly" }) },
              { value: "QUARTERLY", label: intl.formatMessage({ id: "lender.onboarding.review.mandateQuarterly" }) },
            ]}
          />
          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.installmentsLabel" })}
            error={errors.numberOfInstallments}
            required
          >
            <Input
              type="number"
              value={form.numberOfInstallments}
              onChange={(event) => updateField("numberOfInstallments", event.target.value)}
              min={1}
            />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.onboarding.review.installmentAmountLabel" })}>
            <Input
              type="number"
              value={form.installmentAmount}
              onChange={(event) => updateField("installmentAmount", event.target.value)}
              min={0}
              step="any"
              placeholder={intl.formatMessage({ id: "lender.onboarding.review.installmentAmountPlaceholder" })}
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.startDateLabel" })}
            error={errors.installmentStartDate}
            required
          >
            <Input
              type="date"
              value={form.installmentStartDate}
              onChange={(event) => updateField("installmentStartDate", event.target.value)}
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.processingFeeLabel" })}
            error={errors.processingFeePercent}
            required
          >
            <Input
              type="number"
              value={form.processingFeePercent}
              onChange={(event) => updateField("processingFeePercent", event.target.value)}
              min={0}
              step="any"
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.penaltyLabel" })}
            error={errors.installmentPenalty}
            required
          >
            <Input
              type="number"
              value={form.installmentPenalty}
              onChange={(event) => updateField("installmentPenalty", event.target.value)}
              min={0}
              step="any"
            />
          </FormField>
        </div>
      </section>

      <section className="border-t border-border px-6 py-4">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.referencesHeading" />
        </Heading>
        <Text as="p" size="xs" variant="muted" className="mt-1">
          <FormattedMessage id="lender.onboarding.review.referencesHint" />
        </Text>

        {([0, 1] as const).map((index) => (
          <div key={index} className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <FormField
              label={intl.formatMessage(
                { id: "lender.onboarding.review.referenceNameLabel" },
                { number: index + 1 },
              )}
              error={index === 0 ? errors.reference1 : undefined}
              required={index === 0}
            >
              <Input
                value={form.references[index].name}
                onChange={(event) => updateReference(index, { name: event.target.value })}
              />
            </FormField>
            <SelectField
              label={intl.formatMessage({ id: "lender.onboarding.review.referenceRelationLabel" })}
              placeholder={intl.formatMessage({ id: "lender.onboarding.review.referenceRelationPlaceholder" })}
              value={form.references[index].relation}
              onValueChange={(value) => updateReference(index, { relation: value as NomineeRelationship })}
              required={index === 0}
              options={Object.entries(RELATIONSHIP_LABEL_IDS).map(([value, labelId]) => ({
                value,
                label: intl.formatMessage({ id: labelId }),
              }))}
            />
            <FormField
              label={intl.formatMessage({ id: "lender.onboarding.review.referenceContactLabel" })}
              required={index === 0}
            >
              <div className="flex">
                <span
                  className="flex items-center rounded-sm border border-border bg-surface px-3 text-sm text-text-muted"
                  aria-hidden="true"
                >
                  +91
                </span>
                <Input
                  value={form.references[index].contact}
                  onChange={(event) =>
                    updateReference(index, { contact: event.target.value.replace(/\D/g, "").slice(0, 10) })
                  }
                  inputMode="numeric"
                  className="flex-1"
                />
              </div>
            </FormField>
          </div>
        ))}
      </section>

      <div className="flex justify-end px-6 pb-6 pt-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <FormattedMessage id="lender.onboarding.review.savingButton" />
          ) : (
            <FormattedMessage id="lender.onboarding.review.saveAndContinueButton" />
          )}
        </Button>
      </div>
    </form>
  );
}
